import { forwardRef, type CSSProperties } from "react";
import FbkCardShell from "./FbkCardShell";
import { ThemeOverlay } from "./ThemeOverlay";
import { resolveCardTheme } from "./theme";
import {
  defaultFooterLeft,
  resolveTextStyle,
  type CardFormat,
  type LeaderboardCardContent,
} from "./types";

interface LeaderboardCardProps {
  format: CardFormat;
  content: LeaderboardCardContent;
}

const RANK_COLORS: Record<number, { bg: string; text: string }> = {
  1: { bg: "#FFD700", text: "#0A4724" },
  2: { bg: "#C0C0C0", text: "#0A2A1A" },
  3: { bg: "#CD7F32", text: "#FFFFFF" },
};

const LeaderboardCard = forwardRef<HTMLDivElement, LeaderboardCardProps>(
  function LeaderboardCard({ format, content }, ref) {
    const isStory = format === "story";
    const style = content.style ?? {};
    const theme = resolveCardTheme(content.theme ?? "signature", {
      monoColor: content.monoColor,
    });

    // Resolved styles
    const eyebrowStyle: CSSProperties = {
      ...resolveTextStyle(
        style.eyebrow,
        {
          fontFamily: theme.fontEyebrow,
          fontSizePx: 24,
          color: theme.accentColor,
          letterSpacing: "0.4em",
          bold: true,
        },
        format,
      ),
      textTransform: "uppercase",
      marginTop: isStory ? 56 : 32,
    };

    const titleDefaultSize = isStory ? 104 : 84;
    const titleStyle: CSSProperties = {
      ...resolveTextStyle(
        style.headline,
        {
          fontFamily: theme.fontHeadline,
          fontSizePx: titleDefaultSize,
          color: theme.headlineColor,
          letterSpacing: "-0.025em",
          bold: true,
        },
        format,
      ),
      lineHeight: 0.95,
      marginTop: isStory ? 20 : 14,
      marginBottom: isStory ? 40 : 28,
    };

    const titleGoldStyle: CSSProperties = {
      ...resolveTextStyle(
        style.goldHighlight,
        {
          fontFamily: theme.fontHeadline,
          fontSizePx: titleDefaultSize,
          color: theme.accentColor,
          bold: true,
        },
        format,
      ),
      // Inline with the title
      fontSize:
        (style.headline?.fontSizePx ??
          style.goldHighlight?.fontSizePx ??
          titleDefaultSize) * (isStory ? 1 : 0.78),
    };

    // Resolved row text + points styles
    const rowTextResolved = resolveTextStyle(
      style.body,
      {
        fontFamily: theme.fontBody,
        fontSizePx: 28,
        color: theme.headlineColor,
        bold: true,
      },
      format,
    );
    const pointsResolved = resolveTextStyle(
      style.numbers,
      {
        fontFamily: theme.fontBody,
        fontSizePx: 36,
        color: theme.headlineColor,
        bold: true,
      },
      format,
    );

    // Layout constants
    const rowGap = isStory ? 12 : 8;
    const rowPaddingY = isStory ? 16 : 12;
    const rowPaddingX = isStory ? 24 : 20;
    const rowRadius = isStory ? 18 : 14;
    const rankSize = isStory ? 52 : 44;
    const rankFontSize = isStory ? 26 : 22;
    const flagWidth = isStory ? 44 : 36;
    const flagHeight = isStory ? 30 : 24;
    const subtitleFontSize = isStory ? 16 : 14;

    // Defensive: tolerate missing/non-array entries from corrupted state
    const entries = Array.isArray(content.entries)
      ? content.entries.slice(0, 10)
      : [];

    return (
      <FbkCardShell
        ref={ref}
        format={format}
        footerLeft={content.footerLeft || defaultFooterLeft("LEADERBOARD")}
        footerRight={content.hashtags || "#WC2026 · #FBK"}
        header={content.header}
        background={style.background}
        customBaseGradient={theme.customBaseGradient}
        headerOnLight={theme.headerOnLight}
        suppressBrandOverlays={theme.suppressBrandOverlays}
        hideFooter={content.footer?.hide}
      >
        <ThemeOverlay theme={theme} format={format} />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            position: "relative",
            zIndex: 2,
          }}
        >
          <div style={eyebrowStyle}>{content.eyebrow || "LEADERBOARD"}</div>
          <div style={titleStyle}>
            {content.scope === "alltime" ? (
              <>
                All <span style={titleGoldStyle}>Time</span>
              </>
            ) : content.scope === "custom" ? (
              <>
                {content.customTitle}
                {content.customTitleAccent ? (
                  <>
                    {" "}
                    <span style={titleGoldStyle}>
                      {content.customTitleAccent}
                    </span>
                  </>
                ) : null}
              </>
            ) : (
              <>
                Matchweek{" "}
                <span style={titleGoldStyle}>{content.matchweek}</span>
              </>
            )}
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: rowGap,
              flex: 1,
            }}
          >
            {entries.map((entry) => {
              const rankColor = RANK_COLORS[entry.rank];
              const isTop3 = entry.rank <= 3;
              // Force theme accent color for top 3 points (brand rule)
              const pointsStyle: CSSProperties = {
                ...pointsResolved,
                color: isTop3 ? theme.accentColor : (pointsResolved.color as string),
                flexShrink: 0,
              };
              const teamStyle: CSSProperties = {
                ...rowTextResolved,
                lineHeight: 1.1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              };

              return (
                <div
                  key={entry.rank}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: isStory ? 20 : 16,
                    padding: `${rowPaddingY}px ${rowPaddingX}px`,
                    background: theme.headerOnLight
                      ? "rgba(0,0,0,0.05)"
                      : "rgba(255,255,255,0.08)",
                    border: theme.headerOnLight
                      ? "1px solid rgba(0,0,0,0.1)"
                      : "1px solid rgba(255,255,255,0.15)",
                    borderRadius: rowRadius,
                  }}
                >
                  <div
                    style={{
                      width: rankSize,
                      height: rankSize,
                      borderRadius: 9999,
                      background: rankColor
                        ? rankColor.bg
                        : "rgba(255,255,255,0.18)",
                      color: rankColor ? rankColor.text : "#FFFFFF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 800,
                      fontSize: rankFontSize,
                      flexShrink: 0,
                    }}
                  >
                    {entry.rank}
                  </div>

                  {entry.countryIso2 && (
                    <img
                      src={`https://flagcdn.com/w160/${entry.countryIso2.toLowerCase()}.png`}
                      alt=""
                      crossOrigin="anonymous"
                      style={{
                        width: flagWidth,
                        height: flagHeight,
                        objectFit: "cover",
                        borderRadius: 4,
                        flexShrink: 0,
                      }}
                    />
                  )}

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={teamStyle}>{entry.teamName}</div>
                    {entry.username && (
                      <div
                        style={{
                          fontSize: subtitleFontSize,
                          fontWeight: 700,
                          color: theme.subtleTextColor,
                          letterSpacing: "0.05em",
                          marginTop: 2,
                        }}
                      >
                        @{entry.username}
                      </div>
                    )}
                  </div>

                  <div style={pointsStyle}>
                    {entry.points}
                    <span
                      style={{
                        fontSize: subtitleFontSize,
                        fontWeight: 700,
                        color: theme.subtleTextColor,
                        marginLeft: 6,
                        textTransform: "uppercase",
                        letterSpacing: "0.1em",
                      }}
                    >
                      pts
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </FbkCardShell>
    );
  },
);

export default LeaderboardCard;
